"use client";

import type { ComponentProps, ReactNode } from "react";
import { createContext, memo, useContext, useMemo, useState } from "react";
import { BrainIcon, ChevronDownIcon } from "lucide-react";
import { Streamdown } from "streamdown";

import { cn } from "./lib/cn";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";

type ReasoningContextValue = {
  isStreaming: boolean;
  isOpen: boolean;
  duration: number | undefined;
};

const ReasoningContext = createContext<ReasoningContextValue | null>(null);

/** Returns the state shared by a {@link Reasoning} disclosure and its child elements. */
export const useReasoning = () => {
  const context = useContext(ReasoningContext);
  if (!context) {
    throw new Error("Reasoning components must be used within Reasoning");
  }
  return context;
};

export type ReasoningProps = ComponentProps<typeof Collapsible> & {
  isStreaming?: boolean;
  defaultOpen?: boolean;
  duration?: number;
};

/**
 * Collapsible AI reasoning disclosure adapted from the current AI Elements source.
 *
 * Duration remains caller-owned so this shared primitive does not create timer side effects.
 */
export const Reasoning = memo(
  ({
    className,
    isStreaming = false,
    open,
    defaultOpen = true,
    onOpenChange,
    duration,
    children,
    ...props
  }: ReasoningProps) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const isOpen = open ?? uncontrolledOpen;
    const handleOpenChange = (nextOpen: boolean) => {
      if (open === undefined) {
        setUncontrolledOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    };
    const value = useMemo(
      () => ({ duration, isOpen, isStreaming }),
      [duration, isOpen, isStreaming],
    );

    return (
      <ReasoningContext.Provider value={value}>
        <Collapsible
          className={cn("not-prose mb-4", className)}
          onOpenChange={handleOpenChange}
          open={isOpen}
          {...props}
        >
          {children}
        </Collapsible>
      </ReasoningContext.Provider>
    );
  },
);

type ReasoningTriggerContent = Exclude<ReactNode, boolean | null | undefined>;

/** Props for a reasoning trigger with either localized status content or custom children. */
export type ReasoningTriggerProps =
  | (Omit<ComponentProps<typeof CollapsibleTrigger>, "children"> & {
      children: ReasoningTriggerContent;
      getThinkingMessage?: never;
    })
  | (Omit<ComponentProps<typeof CollapsibleTrigger>, "children"> & {
      children?: never;
      getThinkingMessage: (isStreaming: boolean, duration?: number) => ReactNode;
    });

/** Toggle for a {@link Reasoning} disclosure with caller-provided localized or custom status content. */
export const ReasoningTrigger = memo(
  ({ className, children, getThinkingMessage, ...props }: ReasoningTriggerProps) => {
    const { isStreaming, isOpen, duration } = useReasoning();
    return (
      <CollapsibleTrigger
        className={cn(
          "flex w-full items-center gap-2 text-muted-foreground text-sm transition-colors hover:text-foreground",
          className,
        )}
        {...props}
      >
        {children ?? (
          <>
            <BrainIcon className="size-4" />
            {getThinkingMessage?.(isStreaming, duration)}
            <ChevronDownIcon
              className={cn("size-4 transition-transform", isOpen ? "rotate-180" : "rotate-0")}
            />
          </>
        )}
      </CollapsibleTrigger>
    );
  },
);

export type ReasoningContentProps = ComponentProps<typeof CollapsibleContent> & {
  children: string;
};

/** Streamdown-backed content for a {@link Reasoning} disclosure. */
export const ReasoningContent = memo(({ className, children, ...props }: ReasoningContentProps) => (
  <CollapsibleContent
    className={cn(
      "mt-4 text-sm data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-muted-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in",
      className,
    )}
    {...props}
  >
    <Streamdown parseIncompleteMarkdown>{children}</Streamdown>
  </CollapsibleContent>
));

Reasoning.displayName = "Reasoning";
ReasoningTrigger.displayName = "ReasoningTrigger";
ReasoningContent.displayName = "ReasoningContent";
